import { ref, computed } from 'vue'

export function useTasksStore(api) {
  const tasks = ref([])
  const loading = ref(false)
  const selectedTaskId = ref(null)

  const selectedTask = computed(() =>
    tasks.value.find(t => t.id === selectedTaskId.value) || null
  )

  // Group + sort once; cached until task state changes. Per-section lookups
  // are then a cheap Map.get instead of a fresh filter+sort per render.
  const tasksBySectionMap = computed(() => {
    const map = new Map()
    for (const t of tasks.value) {
      const arr = map.get(t.section_id) || map.set(t.section_id, []).get(t.section_id)
      arr.push(t)
    }
    for (const arr of map.values())
      arr.sort((a, b) => a.position - b.position || a.id - b.id)
    return map
  })

  function tasksBySection(sectionId) {
    return computed(() => tasksBySectionMap.value.get(sectionId) || [])
  }

  function tasksByProject(projectId) {
    return computed(() =>
      tasks.value
        .filter(t => t.project_id === projectId && !t.completed_at)
        .sort((a, b) => a.position - b.position || a.id - b.id)
    )
  }

  async function fetchTasks(params = {}) {
    loading.value = true
    try {
      const query = new URLSearchParams()
      if (params.project_id) query.set('project_id', params.project_id)
      if (params.section_id) query.set('section_id', params.section_id)
      if (params.completed) query.set('completed', 'true')
      if (params.all) query.set('all', 'true')
      if (params.q) query.set('q', params.q)

      const url = '/api/tasks' + (query.toString() ? `?${query}` : '')
      const data = await api.get(url)
      tasks.value = data.tasks
    } finally {
      loading.value = false
    }
  }

  async function createTask(attrs) {
    const data = await api.post('/api/tasks', { task: attrs })
    tasks.value.push(data.task)
    return data.task
  }

  async function updateTask(id, attrs) {
    const data = await api.patch(`/api/tasks/${id}`, { task: attrs })
    const idx = tasks.value.findIndex(t => t.id === id)
    if (idx !== -1) tasks.value[idx] = data.task
    return data.task
  }

  async function deleteTask(id) {
    await api.delete(`/api/tasks/${id}`)
    tasks.value = tasks.value.filter(t => t.id !== id)
    if (selectedTaskId.value === id) selectedTaskId.value = null
  }

  async function toggleComplete(id) {
    const task = tasks.value.find(t => t.id === id)
    if (!task) return

    // Optimistic: flip immediately
    const prev = task.completed_at
    task.completed_at = prev ? null : new Date().toISOString()

    // Then persist in background
    try {
      await api.patch(`/api/tasks/${id}`, { task: { completed_at: task.completed_at } })
    } catch {
      task.completed_at = prev // revert on error
    }
  }

  async function moveTask(id, sectionId, position) {
    // Optimistic update
    const task = tasks.value.find(t => t.id === id)
    if (task) {
      task.section_id = sectionId
      task.position = position
    }
    return updateTask(id, { section_id: sectionId, position })
  }

  async function batchUpdateTasks(updates) {
    const data = await api.post('/api/tasks/batch_update', { tasks: updates })
    // Merge updated tasks into local state instead of refetching all
    for (const updated of data.tasks) {
      const idx = tasks.value.findIndex(t => t.id === updated.id)
      if (idx !== -1) tasks.value[idx] = updated
    }
  }

  function selectTask(id) {
    selectedTaskId.value = selectedTaskId.value === id ? null : id
  }

  async function fetchTaskDetail(id) {
    const data = await api.get(`/api/tasks/${id}`)
    const idx = tasks.value.findIndex(t => t.id === id)
    if (idx !== -1) tasks.value[idx] = data.task
    return data.task
  }

  return {
    tasks,
    loading,
    selectedTaskId,
    selectedTask,
    tasksBySection,
    tasksByProject,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    toggleComplete,
    moveTask,
    batchUpdateTasks,
    selectTask,
    fetchTaskDetail,
  }
}
